/**
 * Shared controls for dark demo workbench surfaces.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../../styling/composeClassNames.js";
import { DemoPill, type DemoPillVariant } from "./surfaces.js";
import { getDemoWorkbenchToneColor, type DemoWorkbenchTone } from "./workbench-colors.js";

export type DemoWorkbenchButtonTone = DemoWorkbenchTone;

export interface DemoWorkbenchButtonProps {
  readonly children?: React.ReactNode;
  readonly label?: React.ReactNode;
  readonly ariaLabel?: string;
  readonly active?: boolean;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onClick?: () => void;
  readonly style?: React.CSSProperties;
  readonly title?: string;
  readonly tone?: DemoWorkbenchButtonTone;
  readonly type?: "button" | "submit" | "reset";
}

export function DemoWorkbenchButton({
  children,
  label,
  ariaLabel,
  active,
  className,
  disabled = false,
  onClick,
  style,
  title,
  tone = "default",
  type = "button",
}: DemoWorkbenchButtonProps): React.JSX.Element {
  const color = getDemoWorkbenchToneColor(tone);
  const isActive = active === true;
  return (
    <button
      type={type}
      aria-label={ariaLabel}
      {...(active === undefined ? {} : { "aria-pressed": active })}
      className={className}
      disabled={disabled}
      onClick={onClick}
      title={title}
      style={{
        background: isActive ? color.bg : "rgba(30, 41, 59, 0.72)",
        border: `1px solid ${isActive ? color.border : "rgba(100, 116, 139, 0.42)"}`,
        borderRadius: 8,
        color: isActive ? color.fg : "#e2e8f0",
        cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "inherit",
        fontSize: 11,
        fontWeight: 700,
        minHeight: 30,
        opacity: disabled ? 0.5 : 1,
        padding: "0.35rem 0.65rem",
        transition: "background 140ms ease, border-color 140ms ease, color 140ms ease",
        ...style,
      }}
    >
      {children ?? label}
    </button>
  );
}

export interface DemoWorkbenchControlBarProps {
  readonly children: React.ReactNode;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly style?: React.CSSProperties;
  readonly trailing?: React.ReactNode;
}

export function DemoWorkbenchControlBar({
  children,
  ariaLabel = "Workbench controls",
  className,
  style,
  trailing,
}: DemoWorkbenchControlBarProps): React.JSX.Element {
  return (
    <div
      role="toolbar"
      aria-label={ariaLabel}
      className={className}
      style={{
        alignItems: "center",
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        ...style,
      }}
    >
      {children}
      {trailing !== undefined ? <span style={{ marginLeft: "auto" }}>{trailing}</span> : null}
    </div>
  );
}

export interface DemoWorkbenchPillProps {
  readonly children: React.ReactNode;
  readonly ariaLabel?: string;
  readonly ariaPressed?: boolean;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly mono?: boolean | "math";
  readonly onClick?: () => void;
  readonly title?: string;
  readonly variant?: DemoPillVariant;
}

export function DemoWorkbenchPill({
  children,
  ariaLabel,
  ariaPressed,
  className,
  disabled,
  mono,
  onClick,
  title,
  variant = "default",
}: DemoWorkbenchPillProps): React.JSX.Element {
  return (
    <DemoPill
      variant={variant}
      tone="dark"
      className={cn("border", className)}
      {...(ariaLabel === undefined ? {} : { ariaLabel })}
      {...(ariaPressed === undefined ? {} : { ariaPressed })}
      {...(disabled === undefined ? {} : { disabled })}
      {...(mono === undefined ? {} : { mono })}
      {...(onClick === undefined ? {} : { onClick })}
      {...(title === undefined ? {} : { title })}
    >
      {children}
    </DemoPill>
  );
}

export interface DemoWorkbenchCheckboxProps {
  readonly checked: boolean;
  readonly label: React.ReactNode;
  readonly description?: React.ReactNode;
  readonly className?: string;
  readonly disabled?: boolean;
  readonly onChange: (checked: boolean) => void;
}

export function DemoWorkbenchCheckbox({
  checked,
  label,
  description,
  className,
  disabled = false,
  onChange,
}: DemoWorkbenchCheckboxProps): React.JSX.Element {
  return (
    <label className={cn("flex items-start gap-2 text-sm text-slate-300", className)}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.checked);
        }}
        style={{ accentColor: "#38bdf8", marginTop: 2 }}
      />
      <span>
        <span>{label}</span>
        {description !== undefined ? (
          <span className="mt-1 block text-xs text-slate-400">{description}</span>
        ) : null}
      </span>
    </label>
  );
}

export interface DemoWorkbenchSearchInputProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly ariaLabel?: string;
  readonly className?: string;
  readonly placeholder?: string;
}

export function DemoWorkbenchSearchInput({
  value,
  onChange,
  ariaLabel = "Search",
  className,
  placeholder = "Search...",
}: DemoWorkbenchSearchInputProps): React.JSX.Element {
  return (
    <input
      type="search"
      aria-label={ariaLabel}
      placeholder={placeholder}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      className={cn(
        "min-h-[32px] flex-1 rounded-lg border border-slate-700 bg-slate-950/45 px-3 py-1.5 text-sm text-slate-200 outline-none transition focus:border-sky-400/70",
        className,
      )}
    />
  );
}

export interface DemoWorkbenchSelectOption {
  readonly value: string;
  readonly label: string;
}

export interface DemoWorkbenchSelectProps {
  readonly value: string;
  readonly options: readonly DemoWorkbenchSelectOption[];
  readonly onChange: (value: string) => void;
  readonly ariaLabel?: string;
  readonly className?: string;
}

export function DemoWorkbenchSelect({
  value,
  options,
  onChange,
  ariaLabel = "Select",
  className,
}: DemoWorkbenchSelectProps): React.JSX.Element {
  return (
    <select
      aria-label={ariaLabel}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
      className={cn(
        "min-h-[32px] rounded-lg border border-slate-700 bg-slate-950/45 px-2.5 py-1.5 text-sm text-slate-200 outline-none transition focus:border-sky-400/70",
        className,
      )}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}
